import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Plus,
  Trash2,
  Edit3,
  Package,
  Check,
  X,
  CheckCircle,
  XCircle,
  RotateCcw,
  Tag,
  Percent
} from 'lucide-react';
import { useSocket } from '../context/SocketContext';

const emptyProduct = {
  productId: '',
  customName: '',
  customSku: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  notes: '',
  isOptional: false,
  isIncluded: false
};

function ProductsEditor({ submissionId, projectTypeId, isAdmin = false, onRefresh }) {
  const [products, setProducts] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [useCustomProduct, setUseCustomProduct] = useState(false);
  const { socket } = useSocket();

  // Fetch catalog products for this project type
  const fetchCatalogProducts = async () => {
    if (!projectTypeId) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectTypeId}/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCatalogProducts(data.filter(p => p.isActive !== false));
      }
    } catch (err) {
      console.error('Fetch catalog products error:', err);
    }
  };

  // Fetch quote products
  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Fetch products error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCatalogProducts();
    fetchProducts();
  }, [submissionId, projectTypeId]);

  // Listen to WebSocket events for quote changes
  useEffect(() => {
    if (!socket) return;

    const handleQuoteChanged = (data) => {
      if (data.submissionId === submissionId) {
        fetchProducts();
        if (onRefresh) onRefresh();
      }
    };

    socket.on('quoteChanged', handleQuoteChanged);

    return () => {
      socket.off('quoteChanged', handleQuoteChanged);
    };
  }, [socket, submissionId, onRefresh]);

  const openAddDialog = () => {
    setEditingProduct(null);
    setUseCustomProduct(false);
    setFormData({ ...emptyProduct });
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setUseCustomProduct(!product.productId);
    setFormData({
      productId: product.productId || '',
      customName: product.customName || '',
      customSku: product.customSku || '',
      quantity: product.quantity,
      unitPrice: product.unitPrice,
      discount: product.discount || 0,
      notes: product.notes || '',
      isOptional: product.isOptional || false,
      isIncluded: product.isIncluded || false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validation
    const hasProduct = formData.productId || formData.customName.trim();
    if (!hasProduct || formData.quantity < 1 || formData.unitPrice < 0) return;

    try {
      const token = localStorage.getItem('portalToken');
      
      const dataToSend = {
        productId: useCustomProduct ? null : (formData.productId || null),
        customName: useCustomProduct ? formData.customName : null,
        customSku: useCustomProduct ? formData.customSku : null,
        quantity: formData.quantity,
        unitPrice: formData.unitPrice,
        discount: formData.discount,
        notes: formData.notes,
        isOptional: formData.isOptional,
        isIncluded: formData.isIncluded
      };
      
      if (editingProduct) {
        // Update
        await fetch(`/api/quote-products/${editingProduct.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(dataToSend)
        });
      } else {
        // Create
        await fetch(`/api/submissions/${submissionId}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(dataToSend)
        });
      }

      setDialogOpen(false);
      fetchProducts();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Save product error:', err);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit du devis ?')) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote-products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProducts();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Delete product error:', err);
    }
  };

  // Client accepts or declines an optional product
  const handleAcceptOption = async (productId, accepted) => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/quote-products/${productId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accepted })
      });
      
      if (response.ok) {
        fetchProducts();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Accept option error:', err);
    }
  };

  // Admin resets client's acceptance decision
  const handleResetOption = async (productId) => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/quote-products/${productId}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchProducts();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Reset option error:', err);
    }
  };

  // Calculate line total
  const getLineTotal = (product) => {
    return product.quantity * product.unitPrice * (1 - (product.discount || 0) / 100);
  };

  // Calculate totals
  const baseRequired = products
    .filter(p => !p.isOptional && !p.isIncluded)
    .reduce((sum, p) => sum + getLineTotal(p), 0);
  const acceptedOptions = products
    .filter(p => p.isOptional && p.isAcceptedByClient === true)
    .reduce((sum, p) => sum + getLineTotal(p), 0);
  const totalRequired = baseRequired + acceptedOptions;
  const totalOptional = products
    .filter(p => p.isOptional && p.isAcceptedByClient !== true && p.isAcceptedByClient !== false)
    .reduce((sum, p) => sum + getLineTotal(p), 0);
  const totalIncluded = products
    .filter(p => p.isIncluded)
    .reduce((sum, p) => sum + getLineTotal(p), 0);
  const includedCount = products.filter(p => p.isIncluded).length;
  const acceptedCount = products.filter(p => p.isOptional && p.isAcceptedByClient === true).length;

  // Handle catalog product selection
  const handleProductSelect = (_, product) => {
    if (product) {
      setFormData({
        ...formData,
        productId: product.id,
        unitPrice: product.unitPrice,
        customName: '',
        customSku: ''
      });
    } else {
      setFormData({
        ...formData,
        productId: '',
        unitPrice: 0
      });
    }
  };

  if (loading) {
    return <Typography variant="body2" color="text.secondary">Chargement...</Typography>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Package size={20} color="#db2777" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Matériel / Produits
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={openAddDialog}
            sx={{ borderColor: '#db2777', color: '#db2777', '&:hover': { borderColor: '#db2777', bgcolor: 'rgba(219, 39, 119, 0.1)' } }}
          >
            Ajouter
          </Button>
        )}
      </Box>

      {/* Products List */}
      {products.length > 0 ? (
        <Stack spacing={1.5}>
          {products.map((product) => {
            const productName = product.product?.name || product.customName || 'Produit';
            const productSku = product.product?.sku || product.customSku;
            const lineTotal = getLineTotal(product);
            
            return (
              <Paper
                key={product.id}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 1.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    borderColor: 'rgba(219, 39, 119, 0.3)'
                  }
                }}
              >
                {/* Icon */}
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(219, 39, 119, 0.15)',
                  color: '#db2777',
                  display: 'flex'
                }}>
                  <Package size={16} />
                </Box>

                {/* Info */}
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {productName}
                    </Typography>
                    {productSku && (
                      <Chip
                        label={productSku}
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem', 
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          color: 'text.secondary'
                        }}
                      />
                    )}
                    {product.isIncluded && (
                      <Chip
                        label="Offert"
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem', 
                          bgcolor: 'rgba(16, 185, 129, 0.15)',
                          color: '#10b981',
                          fontWeight: 600
                        }}
                      />
                    )}
                    {product.isOptional && (
                      <Chip
                        label="Optionnel"
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem', 
                          bgcolor: 'rgba(245, 158, 11, 0.15)',
                          color: '#f59e0b',
                          fontWeight: 600
                        }}
                      />
                    )}
                    {product.isOptional && product.isAcceptedByClient === true && (
                      <Chip
                        icon={<CheckCircle size={12} />}
                        label="Accepté"
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem', 
                          bgcolor: 'rgba(16, 185, 129, 0.15)',
                          color: '#10b981',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#10b981' }
                        }}
                      />
                    )}
                    {product.isOptional && product.isAcceptedByClient === false && (
                      <Chip
                        icon={<XCircle size={12} />}
                        label="Refusé"
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem', 
                          bgcolor: 'rgba(239, 68, 68, 0.15)',
                          color: '#ef4444',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#ef4444' }
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {product.quantity} × ${product.unitPrice.toLocaleString()}
                    {product.discount > 0 && ` (-${product.discount}%)`}
                    {product.notes && ` • ${product.notes}`}
                  </Typography>
                </Box>

                {/* Accept/Decline buttons for optional products (for clients) */}
                {product.isOptional && !isAdmin && product.isAcceptedByClient !== true && product.isAcceptedByClient !== false && (
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleAcceptOption(product.id, true)}
                      sx={{ 
                        minWidth: 'auto', 
                        px: 1.5,
                        bgcolor: '#10b981', 
                        '&:hover': { bgcolor: '#059669' },
                        fontSize: '0.75rem'
                      }}
                      startIcon={<Check size={14} />}
                    >
                      Accepter
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleAcceptOption(product.id, false)}
                      sx={{ 
                        minWidth: 'auto', 
                        px: 1.5,
                        borderColor: '#ef4444',
                        color: '#ef4444',
                        '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239, 68, 68, 0.1)' },
                        fontSize: '0.75rem'
                      }}
                      startIcon={<X size={14} />}
                    >
                      Refuser
                    </Button>
                  </Stack>
                )}

                {/* Price */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: product.isIncluded ? '#10b981' : '#db2777', minWidth: 100, textAlign: 'right' }}>
                  {product.isIncluded ? 'Offert' : `$${lineTotal.toLocaleString()}`}
                </Typography>

                {/* Admin Actions */}
                {isAdmin && (
                  <Stack direction="row" spacing={0.5}>
                    {product.isOptional && (product.isAcceptedByClient === true || product.isAcceptedByClient === false) && (
                      <Tooltip title="Réinitialiser la décision du client">
                        <IconButton 
                          size="small" 
                          onClick={() => handleResetOption(product.id)}
                          sx={{ color: '#8b5cf6' }}
                        >
                          <RotateCcw size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => openEditDialog(product)}>
                      <Edit3 size={14} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(product.id)} sx={{ color: '#ef4444' }}>
                      <Trash2 size={14} />
                    </IconButton>
                  </Stack>
                )}
              </Paper>
            );
          })}

          {/* Totals */}
          <Divider sx={{ opacity: 0.1, my: 1 }} />
          <Box sx={{ 
            p: 2, 
            bgcolor: 'rgba(219, 39, 119, 0.05)', 
            borderRadius: 1.5,
            border: '1px solid rgba(219, 39, 119, 0.2)'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Total matériel
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {acceptedCount > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      dont ${acceptedOptions.toLocaleString()} accepté
                    </Typography>
                  )}
                  {totalOptional > 0 && (
                    <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                      + ${totalOptional.toLocaleString()} optionnel en attente
                    </Typography>
                  )}
                  {includedCount > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      + ${totalIncluded.toLocaleString()} offert
                    </Typography>
                  )}
                </Box>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#db2777' }}>
                ${totalRequired.toLocaleString()}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      ) : (
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.01)',
            border: '1px dashed rgba(255, 255, 255, 0.1)',
            borderRadius: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Aucun produit/matériel ajouté
          </Typography>
          {isAdmin && (
            <Button
              variant="text"
              size="small"
              startIcon={<Plus size={14} />}
              onClick={openAddDialog}
              sx={{ mt: 1, color: '#db2777' }}
            >
              Ajouter un produit
            </Button>
          )}
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Toggle: Catalog vs Custom */}
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomProduct}
                  onChange={(e) => {
                    setUseCustomProduct(e.target.checked);
                    if (e.target.checked) {
                      setFormData({ ...formData, productId: '' });
                    } else {
                      setFormData({ ...formData, customName: '', customSku: '' });
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Saisie manuelle
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Produit non présent dans le catalogue
                  </Typography>
                </Box>
              }
            />

            {!useCustomProduct ? (
              /* Catalog Product Selection */
              <Autocomplete
                options={catalogProducts}
                getOptionLabel={(option) => option.name || ''}
                value={catalogProducts.find(p => p.id === formData.productId) || null}
                onChange={handleProductSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produit du catalogue"
                    placeholder="Rechercher un produit..."
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Package size={16} color="#db2777" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.sku && (
                          <Typography variant="caption" color="text.secondary">
                            SKU: {option.sku}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#db2777', fontWeight: 600 }}>
                        ${option.unitPrice}
                      </Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText={catalogProducts.length === 0 ? "Aucun produit dans le catalogue" : "Aucun résultat"}
              />
            ) : (
              /* Custom Product Fields */
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Nom du produit"
                  placeholder="Ex: Téléphone IP, Licence logicielle..."
                  value={formData.customName}
                  onChange={(e) => setFormData({ ...formData, customName: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  size="small"
                  label="SKU / Référence (optionnel)"
                  placeholder="Ex: YEALINK-T46U"
                  value={formData.customSku}
                  onChange={(e) => setFormData({ ...formData, customSku: e.target.value })}
                />
              </>
            )}

            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                type="number"
                label="Quantité"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
                sx={{ width: 120 }}
                required
              />
              <TextField
                size="small"
                type="number"
                label="Prix unitaire"
                value={formData.unitPrice}
                onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ flexGrow: 1 }}
                required
              />
              <TextField
                size="small"
                type="number"
                label="Remise"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  endAdornment: <InputAdornment position="end"><Percent size={14} /></InputAdornment>
                }}
                inputProps={{ min: 0, max: 100 }}
                sx={{ width: 100 }}
              />
            </Stack>

            {/* Line total preview */}
            <Box sx={{ p: 1.5, bgcolor: 'rgba(219, 39, 119, 0.1)', borderRadius: 1, textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Total ligne: 
                <Typography component="span" sx={{ fontWeight: 'bold', color: '#db2777', ml: 1 }}>
                  ${(formData.quantity * formData.unitPrice * (1 - formData.discount / 100)).toLocaleString()}
                </Typography>
              </Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Notes (optionnel)"
              placeholder="Détails supplémentaires..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />

            <Divider sx={{ opacity: 0.1 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isIncluded}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    isIncluded: e.target.checked,
                    isOptional: e.target.checked ? false : formData.isOptional
                  })}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: formData.isIncluded ? '#10b981' : 'inherit' }}>
                    Offert
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Affichera "Offert" au lieu du montant
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isOptional}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    isOptional: e.target.checked,
                    isIncluded: e.target.checked ? false : formData.isIncluded
                  })}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: formData.isOptional ? '#f59e0b' : 'inherit' }}>
                    Optionnel
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Le client pourra choisir d'inclure ce produit
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              (!useCustomProduct && !formData.productId) ||
              (useCustomProduct && !formData.customName.trim()) ||
              formData.quantity < 1 ||
              formData.unitPrice < 0
            }
            sx={{ bgcolor: '#db2777', '&:hover': { bgcolor: '#be185d' } }}
          >
            {editingProduct ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductsEditor;
